import uniqueId from 'lodash/uniqueId'
import {
  array,
  bool,
  string,
  oneOf,
  oneOfType,
  func,
  number,
  any,
  object,
  arrayOf,
  node,
} from 'prop-types'
import { Component } from 'react'
import { AnalyticsConsumer } from '@opallabs/quanta'
import { renderIcon, kuiperClassNames } from '../../utils'
import InputLabel from '../InputLabel'
import './styles.scss'

function remainingLength(maxLength, currentLength) {
  return maxLength - currentLength
}

function asideClasses(maxLength, currentLength, warningFactor) {
  const diff = remainingLength(maxLength, currentLength)
  return kuiperClassNames({
    aside: true,
    max: diff < 0,
    over: diff < 0,
    warning: diff < maxLength * warningFactor,
  })
}

function highlightText(value, maxLength) {
  const textUnder = value.substring(0, maxLength)
  const textOver = value.substring(maxLength) || ''
  return (
    <div className={kuiperClassNames('highlightable')}>
      {textUnder}
      <span className={kuiperClassNames('danger')}>{textOver}</span>
    </div>
  )
}

/**
 * Kuiper's base implementation of an input.
 *
 * @note Don't use this component directly; use one of the derived components
 *   instead.
 *
 * @private
 */
export default class TextInput extends Component {
  static propTypes = {
    ariaLabel: string,
    ariaLabelledBy: string,
    aside: bool,
    autoFocus: bool,
    children: oneOfType([arrayOf(node), node]),
    className: oneOfType([object, string]),
    disabled: bool,
    focusOnMount: bool,
    highlight: bool,
    highlightOnFocus: bool,
    htmlId: string,
    icon: string,
    iconColor: string,
    inputType: string,
    isCurrency: bool,
    maxLength: number,
    name: string,
    onBlur: func,
    onChange: func,
    onFocus: func,
    onKeyDown: func,
    onKeyUp: func,
    placeholder: string,
    readOnly: bool,
    resetOnBlur: bool,
    role: string,
    shouldValidate: bool,
    strict: bool,
    style: object,
    tabIndex: number,
    theme: string,
    // @note This needs to match what `InputLabel` accepts for its `text` prop.
    title: oneOfType([array, string, object]),
    triggerOnKeyPress: oneOfType([object, bool]),
    truncate: bool,
    type: oneOf(['email', 'text', 'password', 'number']),
    valid: bool,
    value: any,
    warningFactor: number,
  }

  static defaultProps = {
    aside: false,
    autoFocus: false,
    name: 'Text Input',
    className: 'textinput',
    shouldValidate: true,
    tabIndex: 0,
    theme: 'dark',
    truncate: false,
    type: 'text',
    value: '',
    warningFactor: 0.2,
  }

  constructor(props) {
    super(props)

    // This is used to set an `id` on the input, so that the input's label
    // element can reference it with the `for` attribute (`<label>`) or
    // `aria-labelledby` (anything that isn't a `<label>`).
    const inputId = props.htmlId || uniqueId(`input-${this.props.type}`)

    this.state = {
      cachedValue: props.value,
      inputId,
    }

    this.inputFocus = this.inputFocus.bind(this)
    this.inputBlur = this.inputBlur.bind(this)
    this.onChange = this.onChange.bind(this)
    this.trackInputElement = this.trackInputElement.bind(this)
    this.focus = this.focus.bind(this)
  }

  componentDidMount() {
    if (this.props.focusOnMount) {
      // This allows inputs in popovers to render fully
      // before we call focus on the element
      setTimeout(() => {
        this.focus()
      }, 0)
      this.inputFocus()
    }
  }

  inputFocus() {
    if (!this.props.disabled) {
      this.setState({
        focused: true,
      })

      if (this.props.onFocus) {
        this.props.onFocus()
      }
    }
  }

  inputBlur(track) {
    return (e) => {
      const value = e.target.value
      if (track) {
        track('blur', { value, inputName: this.props.name })
      }
      this.setState((lastState) => {
        if (this.props.resetOnBlur && value === '') {
          this.props.onChange(lastState.cachedValue)
        } else if (this.props.onBlur) {
          this.props.onBlur(value)
        }
        return {
          focused: false,
          cachedValue:
            this.props.resetOnBlur && value === ''
              ? lastState.cachedValue
              : value,
        }
      })
    }
  }

  onChange(e) {
    const value = e.target.value
    if (value !== this.props.value) {
      this.props.onChange(value, this.props.name)
    }
  }

  onKeyDown = (e) => {
    const disable = ['e', '-', '+', '.']
    if (this.props.type === 'number') {
      if (disable.includes(e.key)) {
        e.preventDefault()
      }
    }

    if (this.props.onKeyDown) {
      this.props.onKeyDown(e)
    }
  }

  onKeyUp = (e) => {
    const { triggerOnKeyPress } = this.props
    if (triggerOnKeyPress && triggerOnKeyPress?.keyCodes.includes(e.keyCode)) {
      e.preventDefault()
      triggerOnKeyPress.triggerFunction(e.keyCode)
      if (triggerOnKeyPress.blurOnTriggered) {
        e.target.blur()
      }
    }

    if (this.props.onKeyUp) this.props.onKeyUp(e)
  }

  trackInputElement(element) {
    if (!this.trackedInputElement) {
      this.trackedInputElement = element
    }
  }

  focus() {
    if (
      this.trackedInputElement?.focus &&
      typeof this.trackedInputElement.focus === 'function'
    ) {
      return this.trackedInputElement.focus()
    }
    if (
      this.trackedInputElement?.refs?.input?.focus &&
      typeof this.trackedInputElement.refs.input.focus === 'function'
    ) {
      return this.trackedInputElement.refs.input.focus()
    }
    return null
  }

  get theme() {
    return this.props.theme
  }

  get value() {
    return this.props.value
  }

  get remaining() {
    return remainingLength(this.props.maxLength, this.value.length)
  }

  get aside() {
    if (this.props.aside && this.props.maxLength) {
      const asideClassString = asideClasses(
        this.props.maxLength,
        this.value.length,
        this.props.warningFactor
      )
      return <aside className={asideClassString}>{this.remaining}</aside>
    }
    return null
  }

  inputProps(track) {
    return {
      'aria-label': !this.props.ariaLabelledBy && this.props.ariaLabel,
      'aria-labelledby': this.props.ariaLabelledBy,
      autoFocus: this.props.autoFocus,
      className: [
        kuiperClassNames('input-element'),
        // @hack override global styles
        'styled',
      ].join(' '),
      disabled: this.props.disabled,
      id: this.state.inputId,
      ref: this.trackInputElement.bind(this),
      type: this.props.type,
      onChange: this.onChange,
      onFocus: (e) => {
        if (this.props.highlightOnFocus && !this.state.focused) {
          e.preventDefault()
          e.target.select()
        }
        this.inputFocus()
      },
      onBlur: this.inputBlur(track),
      onKeyUp: this.onKeyUp,
      value: this.value || '',
      maxLength: (this.props.strict && this.props.maxLength) || undefined,
      style: this.props.style,
      readOnly: this.props.readOnly,
    }
  }

  get inputElement() {
    return (
      <AnalyticsConsumer>
        {({ track }) => (
          <div className={kuiperClassNames('input-area')}>
            <input
              {...this.inputProps(track)}
              onKeyDown={(event) => this.onKeyDown(event)}
              placeholder={this.props.placeholder}
              role={this.props.role}
              tabIndex={this.props.tabIndex}
            />
            {this.valueSizer}
          </div>
        )}
      </AnalyticsConsumer>
    )
  }

  get icon() {
    if (this.props.icon) {
      const ariaMode = this.props.placeholder ? 'hidden' : 'presentational'
      return renderIcon(this.props.icon, { ariaMode })
    }
    return null
  }

  get placeholder() {
    return (
      <div className={kuiperClassNames('input-placeholder')}>
        {this.props.placeholder}
      </div>
    )
  }

  get sizerValue() {
    if (!this.props.value || this.props.value === '') {
      return this.props.placeholder
    }
    return this.props.value
  }

  get valueSizer() {
    return (
      <div className={kuiperClassNames('input-value-sizer')}>
        {this.sizerValue}
      </div>
    )
  }

  get input() {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        className={kuiperClassNames('input-container', {
          'input-with-aside': this.props.aside,
        })}
        onClick={this.inputFocus}
      >
        {this.icon}
        {this.inputElement}
        {this.props.children}
        {this.aside}
      </div>
    )
  }

  get highlightableDiv() {
    if (this.props.maxLength && !this.props.truncate) {
      return highlightText(this.props.value, this.props.maxLength)
    }

    return null
  }

  themes(...addons) {
    const { theme } = this.props
    if (theme) {
      return [...theme.split(' '), ...addons].map((name) => `theme-${name}`)
    }
    return undefined
  }

  classNames(context = this) {
    const {
      className,
      disabled,
      expandable,
      highlight,
      isCurrency,
      iconColor,
      inputType,
      strict,
      type,
    } = context.props
    const { value } = context
    const { focused } = context.state
    return kuiperClassNames('input', className, this.themes(), {
      [`color-${iconColor}`]: iconColor,
      [`type-${type}`]: type,
      [`input-${inputType}`]: inputType,
      currency: isCurrency,
      filled: value && value !== '',
      strict,
      expandable,
      focused,
      disabled,
      highlight,
    })
  }

  render() {
    const { shouldValidate, title, valid } = this.props
    return (
      <div className={this.classNames(this)}>
        <InputLabel
          inputId={this.state.inputId}
          shouldValidate={shouldValidate}
          text={title}
          valid={valid}
        />
        {this.input}
        {this.highlightableDiv}
      </div>
    )
  }
}
